import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY_PREFIX = 'gym_tracker_routine_';

export const useLocalRoutinePersistence = () => {
    const [unsavedChanges, setUnsavedChanges] = useState({}); // { routineId: boolean }

    // Save routine to local storage
    const saveLocal = useCallback((routineId, data, isDirty = true) => {
        if (!routineId || !data) return;

        try {
            const key = `${STORAGE_KEY_PREFIX}${routineId}`;
            const payload = {
                data,
                timestamp: new Date().toISOString(),
                isDirty
            };
            localStorage.setItem(key, JSON.stringify(payload));

            setUnsavedChanges(prev => ({
                ...prev,
                [routineId]: isDirty
            }));

            // Notify other hook instances
            window.dispatchEvent(new CustomEvent('routine-local-storage-updated'));
        } catch (error) {
            console.error("Error saving to localStorage:", error);
            // Handle quota exceeded error if necessary
        }
    }, []);

    // Load routine from local storage
    const loadLocal = useCallback((routineId) => {
        if (!routineId) return null;

        try {
            const key = `${STORAGE_KEY_PREFIX}${routineId}`;
            const item = localStorage.getItem(key);
            if (!item) return null;

            const parsed = JSON.parse(item);
            return parsed; // returns { data, timestamp, isDirty }
        } catch (error) {
            console.error("Error loading from localStorage:", error);
            return null;
        }
    }, []);

    // Clear local storage for a specific routine (after sync)
    const clearLocal = useCallback((routineId) => {
        if (!routineId) return;

        try {
            const key = `${STORAGE_KEY_PREFIX}${routineId}`;
            localStorage.removeItem(key);

            setUnsavedChanges(prev => {
                const newState = { ...prev };
                delete newState[routineId];
                return newState;
            });

            // Notify other hook instances
            window.dispatchEvent(new CustomEvent('routine-local-storage-updated'));
        } catch (error) {
            console.error("Error clearing localStorage:", error);
        }
    }, []);

    // Check if a routine has local unsaved changes
    const hasUnsavedChanges = useCallback((routineId) => {
        return !!unsavedChanges[routineId];
    }, [unsavedChanges]);

    // Initialize unsaved changes state on mount and listen for changes
    useEffect(() => {
        const checkAll = () => {
            const initialUnsaved = {};
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(STORAGE_KEY_PREFIX)) {
                    try {
                        const item = JSON.parse(localStorage.getItem(key));
                        if (item && item.isDirty) {
                            const routineId = key.replace(STORAGE_KEY_PREFIX, '');
                            initialUnsaved[routineId] = true;
                        }
                    } catch {
                        console.warn("Corrupt local storage item:", key);
                    }
                }
            });
            setUnsavedChanges(initialUnsaved);
        };

        // Initial check
        checkAll();

        // Listen for updates from other hook instances in same window
        const handleStorageUpdate = () => {
            checkAll();
        };

        window.addEventListener('routine-local-storage-updated', handleStorageUpdate);
        return () => window.removeEventListener('routine-local-storage-updated', handleStorageUpdate);
    }, []);

    // Get all dirty routines from local storage
    const getAllDirtyRoutines = useCallback(() => {
        const dirty = [];
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(STORAGE_KEY_PREFIX)) {
                try {
                    const item = JSON.parse(localStorage.getItem(key));
                    if (item && item.isDirty) {
                        const routineId = key.replace(STORAGE_KEY_PREFIX, '');
                        dirty.push({ id: routineId, ...item });
                    }
                } catch {
                    // Ignore
                }
            }
        });
        return dirty;
    }, []);

    return {
        saveLocal,
        loadLocal,
        clearLocal,
        hasUnsavedChanges,
        unsavedChanges,
        getAllDirtyRoutines
    };
};
